/**
 * Browser-navigation guard for unsaved work (milestone 7.3).
 *
 * Two jobs, in order of preference:
 *
 * 1. **Flush.** When the tab is hidden or being torn down, save immediately instead of waiting out
 *    the autosave debounce. `visibilitychange` is the reliable moment for this — a hidden tab may be
 *    discarded without ever firing `pagehide`, and mobile browsers frequently do exactly that.
 * 2. **Warn.** If work is still unsaved when the page is about to go, ask the browser to show its
 *    "leave site?" confirmation so the navigation can be cancelled.
 *
 * The warning is deliberately conditional: a dialog on every reload trains people to dismiss it, and
 * after a successful autosave there is nothing to lose. The message itself is not ours to choose —
 * browsers have shown their own fixed text since 2017 and ignore any string we supply.
 */

/** What a guarded event carries. Only the two members the guard touches are required. */
export interface GuardEvent {
  preventDefault: () => void;
  returnValue?: unknown;
}

export type GuardListener = (event: GuardEvent) => unknown;

/**
 * The slice of `window`/`document` this needs, so tests can pass a stub in a DOM-less environment.
 * Declared with method shorthand so the real `Window` structurally satisfies it.
 */
export interface EventTargetLike {
  addEventListener(type: string, listener: GuardListener, options?: unknown): void;
  removeEventListener(type: string, listener: GuardListener, options?: unknown): void;
}

export interface UnloadGuardDeps {
  /** Whether leaving now would lose work. Called on every navigation attempt. */
  hasUnsavedChanges: () => boolean;
  /** Best-effort save, run when the tab is hidden or unloading. */
  onFlush?: () => void;
  /** Defaults to `window`. */
  target?: EventTargetLike;
  /** Defaults to `document`; supplies `visibilityState`. */
  documentTarget?: (EventTargetLike & { visibilityState?: string }) | undefined;
}

export class UnloadGuard {
  private readonly target: EventTargetLike | null;
  private readonly documentTarget: (EventTargetLike & { visibilityState?: string }) | null;

  private readonly onBeforeUnload = (event: GuardEvent): string | undefined => {
    if (!this.deps.hasUnsavedChanges()) return undefined;
    // All three are required across engines: `preventDefault` is the modern signal, `returnValue`
    // is what older ones read, and Safari historically wanted the return value. The string itself
    // is never displayed — browsers show their own wording.
    event.preventDefault();
    event.returnValue = UNSAVED_WORK_MESSAGE;
    return UNSAVED_WORK_MESSAGE;
  };

  private readonly onVisibilityChange = (): void => {
    if (this.documentTarget?.visibilityState === 'hidden') this.deps.onFlush?.();
  };

  private readonly onPageHide = (): void => {
    this.deps.onFlush?.();
  };

  constructor(private readonly deps: UnloadGuardDeps) {
    this.target = deps.target ?? (typeof window === 'undefined' ? null : window);
    this.documentTarget =
      deps.documentTarget ?? (typeof document === 'undefined' ? null : document);

    this.target?.addEventListener('beforeunload', this.onBeforeUnload);
    this.target?.addEventListener('pagehide', this.onPageHide);
    this.documentTarget?.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Whether a navigation right now would be challenged. Exposed for tests and the status bar. */
  get isArmed(): boolean {
    return this.deps.hasUnsavedChanges();
  }

  dispose(): void {
    this.target?.removeEventListener('beforeunload', this.onBeforeUnload);
    this.target?.removeEventListener('pagehide', this.onPageHide);
    this.documentTarget?.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}

/** Legacy `returnValue` payload. Modern browsers show their own wording instead. */
export const UNSAVED_WORK_MESSAGE = 'This site has unsaved changes.';
